/**
 * Assessments feature components (extracted from App.js, Feb 2026).
 *
 * Three tightly-coupled inline components that previously lived in
 * App.js (lines 81295-82033, ~740 lines) — now extracted as named
 * exports so both the legacy chart AND the V2 chart can mount the
 * same code without duplicating ~735 lines.
 *
 * Components:
 *   • `RecentAssessmentScores`  — recent scored-assessment list + detail dialog
 *   • `AssessmentsTabContent`   — wrapper: header, progress chart, recent scores
 *   • `NewAssessmentButton`     — "New Assessment" button + the multi-step dialog
 *
 * Logic is preserved verbatim from App.js — no behavior changes during
 * the extraction.  The only difference is the imports are now
 * resolved from explicit module paths instead of App.js's giant
 * top-level import block.
 *
 * Token is accepted as a prop (instead of read from a context) so
 * legacy callers (App.js) can keep passing `token` from their
 * existing useAuth() chain without code changes.  V2 callers should
 * pull the token from `useAuth()` and pass it the same way.
 */
import React, { Suspense, lazy } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, ChevronRight, ChevronDown, AlertTriangle,
  Plus, Search, X, Edit, Send, Clock, FileDown, CheckCircle2,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Collapsible, CollapsibleTrigger, CollapsibleContent,
} from '../ui/collapsible';
import { Alert, AlertDescription } from '../ui/alert';
import { DebouncedInput } from '../ui/debounced-textarea';
import { getOptionLabel } from '../../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssessmentProgressChart = lazy(
  () => import('../AssessmentProgressChart').then(m => ({ default: m.AssessmentProgressChart }))
);

// ────────────────────────────────────────────────────────────────────
// Component: RecentAssessmentScores
// Recent scored-assessment list + per-row detail dialog.
// ────────────────────────────────────────────────────────────────────
export const RecentAssessmentScores = ({ clientId, token }) => {
  const [scores, setScores] = React.useState([]);
  const [pendingRequests, setPendingRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedAssessment, setSelectedAssessment] = React.useState(null);
  const [savingPdfId, setSavingPdfId] = React.useState(null);

  const savePdfToChart = async (scoreId) => {
    setSavingPdfId(scoreId);
    try {
      const { data } = await axios.post(
        `${API}/assessments/scores/${scoreId}/save-pdf-to-chart`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setScores((prev) => prev.map((s) => (
        s.id === scoreId ? { ...s, chart_document_id: data.document_id } : s
      )));
      toast.success(data.already_saved ? 'Already filed in chart' : 'PDF saved to chart (Documents tab)');
    } catch (err) {
      console.error('Failed to save assessment PDF:', err);
      toast.error('Failed to save PDF to chart');
    } finally {
      setSavingPdfId(null);
    }
  };

  const formatAssessmentDateTime = (dateStr, completedAt) => {
    if (completedAt) {
      try {
        const dt = new Date(completedAt);
        return dt.toLocaleDateString('en-US', {
          month: 'numeric', day: 'numeric', year: 'numeric'
        }) + ' at ' + dt.toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit', hour12: true
        });
      } catch (e) { /* fall back */ }
    }
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  };

  React.useEffect(() => {
    const fetchScores = async () => {
      if (!clientId || !token) {
        setLoading(false);
        return;
      }
      try {
        const [scoresRes, pendingRes] = await Promise.all([
          axios.get(
            `${API}/clients/${clientId}/assessment-scores?limit=10`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${API}/clients/${clientId}/assessment-requests`,
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => ({ data: [] })),
        ]);
        setScores(scoresRes.data);
        setPendingRequests(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      } catch (err) {
        console.error('Failed to fetch assessment scores:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchScores();
  }, [clientId, token]);

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  if (scores.length === 0 && pendingRequests.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>No assessment scores recorded yet</p>
        <p className="text-sm">Complete a scored assessment to start tracking progress</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {pendingRequests.map((req) => (
          <div
            key={req.id}
            className="flex items-center justify-between p-3 border border-amber-200 bg-amber-50/60 rounded-lg"
            data-testid={`assessment-pending-row-${req.id}`}
          >
            <div className="flex-1">
              <div className="font-medium">{req.template_name}</div>
              <div className="text-sm text-gray-500">
                Sent {req.requested_at ? new Date(req.requested_at).toLocaleDateString() : ''}
                {req.requested_by_name ? ` by ${req.requested_by_name}` : ''}
                {req.expires_at ? ` • expires ${new Date(req.expires_at).toLocaleDateString()}` : ''}
              </div>
            </div>
            <Badge
              variant="outline"
              className="min-w-[80px] justify-center border-amber-400 text-amber-700 bg-amber-100"
            >
              <Clock className="h-3 w-3 mr-1" />
              {req.status === 'in_progress' ? 'In Progress' : 'Pending'}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid={`assessment-recall-btn-${req.id}`}
              onClick={async () => {
                if (!window.confirm(`Recall "${req.template_name}"? It will disappear from the client's portal. (The client hasn't completed it.)`)) return;
                try {
                  const res = await axios.post(`${API}/client-portal/assessment-requests/${req.id}/cancel`);
                  toast.success(res.data?.message || 'Assessment recalled');
                  setPendingRequests((prev) => prev.filter((r) => r.id !== req.id));
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to recall assessment');
                }
              }}
            >
              Recall
            </Button>
          </div>
        ))}
        {scores.map((score) => (
          <div
            key={score.id}
            className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors hover:border-blue-300 hover:shadow-sm"
            onClick={() => setSelectedAssessment(score)}
            title="Click to view details"
            data-testid={`assessment-score-row-${score.id}`}
          >
            <div className="flex-1">
              <div className="font-medium">{score.template_name}</div>
              <div className="text-sm text-gray-500 flex items-center gap-1.5 flex-wrap">
                <span>{formatAssessmentDateTime(score.assessment_date, score.completed_at)}</span>
                {score.completed_by === 'client' ? (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-300 text-purple-700 bg-purple-50" data-testid={`assessment-self-admin-${score.id}`}>
                    Self-Administered
                  </Badge>
                ) : (
                  <span>• by {score.completed_by_name}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: score.severity_color }}>
                  {score.total_score}
                </div>
                <div className="text-xs text-gray-500">/ {score.max_possible_score}</div>
              </div>
              <Badge
                style={{ backgroundColor: score.severity_color, color: 'white' }}
                className="min-w-[80px] justify-center"
              >
                {score.severity_label}
              </Badge>
              {score.chart_document_id ? (
                <span
                  className="inline-flex items-center gap-1 text-xs text-green-700"
                  title="A PDF copy of this result is filed on the Documents tab"
                  data-testid={`assessment-in-chart-${score.id}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> In chart
                </span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-gray-500 hover:text-teal-700"
                  title="Save a PDF copy of this result to the client's chart (Documents tab)"
                  disabled={savingPdfId === score.id}
                  onClick={(e) => { e.stopPropagation(); savePdfToChart(score.id); }}
                  data-testid={`assessment-save-pdf-${score.id}`}
                >
                  <FileDown className="h-4 w-4" />
                </Button>
              )}
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!selectedAssessment} onOpenChange={(open) => !open && setSelectedAssessment(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedAssessment?.template_name}
            </DialogTitle>
            <DialogDescription>
              Score summary and individual responses for this assessment.
            </DialogDescription>
          </DialogHeader>

          {selectedAssessment && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Date & Time</span>
                  <span className="font-medium">{formatAssessmentDateTime(selectedAssessment.assessment_date, selectedAssessment.completed_at)}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Completed By</span>
                  {selectedAssessment.completed_by === 'client' ? (
                    <Badge variant="outline" className="border-purple-300 text-purple-700 bg-purple-50" data-testid="assessment-detail-self-admin">
                      Self-Administered
                    </Badge>
                  ) : (
                    <span className="font-medium">{selectedAssessment.completed_by_name}</span>
                  )}
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Total Score</span>
                  <span className="text-xl font-bold">{selectedAssessment.total_score} / {selectedAssessment.max_possible_score}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Severity</span>
                  <Badge
                    style={{
                      backgroundColor: selectedAssessment.severity_color + '20',
                      color: selectedAssessment.severity_color,
                      borderColor: selectedAssessment.severity_color
                    }}
                  >
                    {selectedAssessment.severity_label}
                  </Badge>
                </div>
              </div>

              {selectedAssessment.field_scores && Object.keys(selectedAssessment.field_scores).length > 0 ? (
                <div>
                  <h4 className="font-medium mb-3 text-sm text-gray-700">Individual Responses</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedAssessment.field_scores).map(([fieldId, fieldData]) => (
                      <div key={fieldId} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
                        <div className="flex-1 pr-4">
                          <p className="text-sm font-medium">{fieldData.field_name || fieldId}</p>
                          <p className="text-sm text-gray-600">
                            {typeof fieldData.value === 'object'
                              ? (fieldData.value?.label || fieldData.value?.value || JSON.stringify(fieldData.value))
                              : String(fieldData.value ?? '')}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-medium text-sm">
                            {typeof fieldData.score === 'object' ? (fieldData.score?.value ?? 0) : (fieldData.score ?? 0)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic text-center py-4">
                  Detailed responses not available for this assessment
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ────────────────────────────────────────────────────────────────────
// Component: AssessmentsTabContent
// Tab wrapper: header + New-Assessment button, progress chart,
// collapsible recent scores.  `refreshKey` causes the chart + scores
// to remount after a new assessment is submitted.
// ────────────────────────────────────────────────────────────────────
export const AssessmentsTabContent = ({ clientId, token }) => {
  const [refreshKey, setRefreshKey] = React.useState(0);

  const handleAssessmentCreated = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Assessment Tracking</h3>
        <NewAssessmentButton
          clientId={clientId}
          token={token}
          onAssessmentCreated={handleAssessmentCreated}
        />
      </div>

      <Suspense fallback={<div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">Loading chart…</div>}>
        <AssessmentProgressChart
          key={`chart-${refreshKey}`}
          clientId={clientId}
          token={token}
          height={300}
        />
      </Suspense>

      <Collapsible defaultOpen={false}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Recent Assessment Scores</CardTitle>
                <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <RecentAssessmentScores
                key={`scores-${refreshKey}`}
                clientId={clientId}
                token={token}
              />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Component: NewAssessmentButton
// "New Assessment" button + multi-step dialog flow:
//   1) Select Template (categorized + searchable)
//   2) Choose Delivery (Fill Now vs Send to Client portal)
//   3) Fill Form (provider-administered)
//   4) Results
// ────────────────────────────────────────────────────────────────────
const ASSESSMENT_CATEGORIES = {
  'Depression': {
    icon: '🧠',
    patterns: ['phq-9', 'phq-2', 'phq-a', 'bdi', 'beck depression', 'ham-d', 'hamilton depression', 'gds', 'geriatric depression', 'epds', 'edinburgh', 'burns depression', 'zung depression', 'sds', 'calgary']
  },
  'Anxiety': {
    icon: '😰',
    patterns: ['gad-7', 'gad-2', 'bai', 'beck anxiety', 'ham-a', 'hamilton anxiety', 'scared', 'burns anxiety', 'zung anxiety', 'sas']
  },
  'Combined Mood': {
    icon: '📊',
    patterns: ['dass-21', 'dass21', 'depression anxiety stress']
  },
  'Bipolar/Mania': {
    icon: '🎭',
    patterns: ['mdq', 'mood disorder', 'ymrs', 'young mania', 'asrm', 'altman']
  },
  'Trauma/PTSD': {
    icon: '💔',
    patterns: ['pcl-5', 'pcl5', 'ptsd', 'pc-ptsd', 'ace', 'adverse childhood', 'trauma']
  },
  'Substance Use': {
    icon: '🍺',
    patterns: ['audit', 'cage', 'dast', 'drug abuse', 'crafft', 'mast', 'michigan alcohol', 'ciwa', 'cows', 'opioid withdrawal', 'socrates', 'substance']
  },
  'Suicide Risk': {
    icon: '⚠️',
    patterns: ['c-ssrs', 'columbia suicide', 'sbq-r', 'suicide behavior', 'suicide risk']
  },
  'ADHD': {
    icon: '🎯',
    patterns: ['adhd', 'asrs', 'attention deficit', 'snap-iv', 'vanderbilt', 'conners']
  },
  'Cognitive': {
    icon: '🧩',
    patterns: ['moca', 'montreal cognitive', 'mmse', 'mini mental', 'slums', 'cognitive']
  },
  'Psychosis': {
    icon: '👁️',
    patterns: ['bprs', 'brief psychiatric', 'panss', 'positive and negative']
  },
  'Other Clinical': {
    icon: '📋',
    patterns: ['phq-15', 'somatic', 'isi', 'insomnia', 'whodas', 'disability', 'ocd', 'y-bocs', 'bis-11', 'impulsive', 'des', 'dissociative', 'k6', 'kessler']
  }
};

const categorizeTemplate = (template) => {
  const name = (template.name || '').toLowerCase();
  for (const [category, config] of Object.entries(ASSESSMENT_CATEGORIES)) {
    if (config.patterns.some(p => name.includes(p))) {
      return category;
    }
  }
  return 'Other Clinical';
};

export const NewAssessmentButton = ({ clientId, token, onAssessmentCreated }) => {
  const [open, setOpen] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [deliveryMode, setDeliveryMode] = React.useState(null);
  const [formData, setFormData] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [showResults, setShowResults] = React.useState(false);
  const [scoreResult, setScoreResult] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedCategories, setExpandedCategories] = React.useState(new Set(['Depression', 'Anxiety']));
  // Jun 2026 — intake-packet linkage: when the client has an intake
  // bundle in their chart, offer to send the assessment as part of it.
  const [intakeBundles, setIntakeBundles] = React.useState([]);
  const [includeInBundle, setIncludeInBundle] = React.useState(false);
  const [selectedBundleId, setSelectedBundleId] = React.useState(null);

  React.useEffect(() => {
    if (!open || !clientId || !token) return;
    axios.get(`${API}/clients/${clientId}/intake-bundles`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => {
        const list = r.data?.bundles || [];
        setIntakeBundles(list);
        setSelectedBundleId(list[0]?.bundle_id || null);
      })
      .catch(() => setIntakeBundles([]));
  }, [open, clientId, token]);

  React.useEffect(() => {
    const fetchTemplates = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/assessment-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(response.data);
      } catch (err) {
        console.error('Failed to fetch assessment templates:', err);
      }
    };
    if (open) fetchTemplates();
  }, [open, token]);

  React.useEffect(() => {
    const fetchFullTemplate = async () => {
      if (!selectedTemplate?.id || !token || deliveryMode !== 'fill_now') return;
      try {
        const response = await axios.get(`${API}/form-templates/${selectedTemplate.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedTemplate(response.data);
        setFormData({});
      } catch (err) {
        console.error('Failed to fetch template details:', err);
      }
    };
    if (selectedTemplate?.id && !selectedTemplate.form_fields && deliveryMode === 'fill_now') {
      fetchFullTemplate();
    }
  }, [selectedTemplate?.id, selectedTemplate?.form_fields, token, deliveryMode]);

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedTemplate || !clientId) return;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/assessments/calculate-score`, {
        client_id: clientId,
        template_id: selectedTemplate.id,
        form_data: formData,
        assessment_date: new Date().toISOString().split('T')[0]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setScoreResult(response.data);
      setShowResults(true);
      toast.success('Assessment completed and scored!');
      if (onAssessmentCreated) onAssessmentCreated();
    } catch (err) {
      console.error('Failed to submit assessment:', err);
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendToClient = async () => {
    if (!selectedTemplate || !clientId) return;
    setSubmitting(true);
    try {
      const payload = {
        client_id: clientId,
        template_id: selectedTemplate.id
      };
      if (includeInBundle && selectedBundleId) {
        payload.intake_bundle_id = selectedBundleId;
      }
      await axios.post(`${API}/client-portal/send-assessment`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(includeInBundle && selectedBundleId
        ? 'Assessment sent as part of the intake packet!'
        : 'Assessment sent to client portal!');
      // Refresh the scores list so the new request shows as Pending
      onAssessmentCreated?.();
      handleClose();
    } catch (err) {
      console.error('Failed to send assessment:', err);
      toast.error('Failed to send assessment to client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (showResults && onAssessmentCreated) {
      onAssessmentCreated();
    }
    setOpen(false);
    setSelectedTemplate(null);
    setDeliveryMode(null);
    setFormData({});
    setShowResults(false);
    setScoreResult(null);
    setSearchQuery('');
    setIncludeInBundle(false);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setDeliveryMode(null);
  };

  const scoredFields = selectedTemplate?.form_fields?.filter(f => f.is_scored) || [];
  const allFieldsAnswered = scoredFields.every(f => {
    const fieldId = f.field_id || f.id;
    return formData[fieldId] !== undefined && formData[fieldId] !== '';
  });

  const getCurrentStep = () => {
    if (showResults) return 'results';
    if (!selectedTemplate) return 'select_template';
    if (!deliveryMode) return 'choose_delivery';
    if (deliveryMode === 'fill_now') return 'fill_form';
    return 'select_template';
  };

  const currentStep = getCurrentStep();

  // Memoize the filtered + categorized templates for the select step
  // (cheap, but avoids re-running on every keystroke during typing).
  const filteredTemplates = React.useMemo(
    () => templates.filter(t =>
      !searchQuery
      || t.name?.toLowerCase().includes(searchQuery.toLowerCase())
      || t.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [templates, searchQuery],
  );

  const categorized = React.useMemo(() => {
    const grouped = {};
    filteredTemplates.forEach(template => {
      const category = categorizeTemplate(template);
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(template);
    });
    return grouped;
  }, [filteredTemplates]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white"
        data-testid="new-assessment-btn"
      >
        <Plus className="h-4 w-4" />
        New Assessment
      </Button>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {currentStep === 'results' ? 'Assessment Results' :
               currentStep === 'fill_form' ? selectedTemplate?.name :
               currentStep === 'choose_delivery' ? 'How would you like to administer this assessment?' :
               'Select Assessment'}
            </DialogTitle>
            <DialogDescription>
              {currentStep === 'results' ? 'Review the scored result and any clinical alerts before closing.' :
               currentStep === 'fill_form' ? 'Answer each scored item. Submit to compute the score.' :
               currentStep === 'choose_delivery' ? 'Choose whether you will administer this assessment now or send it to the client portal.' :
               'Pick a scored assessment template to begin.'}
            </DialogDescription>
          </DialogHeader>

          {currentStep === 'results' && scoreResult && (
            <div className="space-y-4">
              <div className="text-center p-6 rounded-lg" style={{ backgroundColor: `${scoreResult.severity_color}15` }}>
                <div className="text-5xl font-bold mb-2" style={{ color: scoreResult.severity_color }}>
                  {scoreResult.total_score}
                </div>
                <div className="text-gray-500">out of {scoreResult.max_possible_score}</div>
                <Badge
                  className="mt-3 text-lg px-4 py-1"
                  style={{ backgroundColor: scoreResult.severity_color, color: 'white' }}
                >
                  {scoreResult.severity_label}
                </Badge>
              </div>

              {scoreResult.clinical_alerts?.length > 0 && (
                <Alert variant="warning" className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    {scoreResult.clinical_alerts.map((alert, idx) => (
                      <p key={idx}>{alert}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={handleClose}>Close</Button>
                <Button onClick={() => {
                  setShowResults(false);
                  setScoreResult(null);
                  setSelectedTemplate(null);
                  setDeliveryMode(null);
                  setFormData({});
                }}>
                  Add Another Assessment
                </Button>
              </div>
            </div>
          )}

          {currentStep === 'select_template' && (
            <div className="space-y-3">
              {templates.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No scored assessments configured</p>
              ) : (
                <>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <DebouncedInput
                      placeholder="Search assessments..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {searchQuery && (
                    <p className="text-sm text-gray-500 mb-2">
                      Found {filteredTemplates.length} assessment{filteredTemplates.length !== 1 ? 's' : ''} matching &ldquo;{searchQuery}&rdquo;
                    </p>
                  )}

                  <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                    {Object.entries(ASSESSMENT_CATEGORIES).map(([category, config]) => {
                      const categoryTemplates = categorized[category] || [];
                      if (categoryTemplates.length === 0) return null;

                      const isExpanded = expandedCategories.has(category) || searchQuery.length > 0;

                      return (
                        <div key={category} className="border rounded-lg overflow-hidden">
                          <button
                            onClick={() => !searchQuery && toggleCategory(category)}
                            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                          >
                            <div className="flex items-center gap-2">
                              <span>{config.icon}</span>
                              <span className="font-medium">{category}</span>
                              <Badge variant="secondary" className="text-xs">
                                {categoryTemplates.length}
                              </Badge>
                            </div>
                            {!searchQuery && (
                              <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            )}
                          </button>

                          {isExpanded && (
                            <div className="divide-y">
                              {categoryTemplates.map(template => (
                                <div
                                  key={template.id}
                                  className="p-3 cursor-pointer hover:bg-blue-50 transition-colors"
                                  onClick={() => handleSelectTemplate(template)}
                                  data-testid={`assessment-template-${template.id}`}
                                >
                                  <div className="font-medium text-sm">{template.name}</div>
                                  {template.description && (
                                    <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{template.description}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {currentStep === 'choose_delivery' && (
            <div className="space-y-4">
              <p className="text-gray-600">
                <strong>{selectedTemplate?.name}</strong>
              </p>

              <div className="grid grid-cols-1 gap-3">
                <div
                  className="p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-400 transition-colors"
                  onClick={() => setDeliveryMode('fill_now')}
                  data-testid="delivery-fill-now"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Edit className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium">Fill Out Now</div>
                      <div className="text-sm text-gray-500">Provider administers the assessment now</div>
                    </div>
                  </div>
                </div>

                <div
                  className="p-4 border-2 rounded-lg cursor-pointer hover:bg-green-50 hover:border-green-400 transition-colors"
                  onClick={() => handleSendToClient()}
                  data-testid="delivery-send-to-client"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Send className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium">Send to Client Portal</div>
                      <div className="text-sm text-gray-500">Client completes the assessment themselves</div>
                    </div>
                  </div>
                  {intakeBundles.length > 0 && (
                    <div
                      className="mt-3 pt-3 border-t border-dashed"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="include-in-intake-bundle-label">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={includeInBundle}
                          onChange={(e) => setIncludeInBundle(e.target.checked)}
                          data-testid="include-in-intake-bundle-checkbox"
                        />
                        <span>
                          Send as part of intake packet
                          {intakeBundles.length === 1 && (
                            <span className="text-gray-500"> ({intakeBundles[0].bundle_name})</span>
                          )}
                        </span>
                      </label>
                      {includeInBundle && intakeBundles.length > 1 && (
                        <select
                          className="mt-2 w-full border rounded-md px-2 py-1.5 text-sm"
                          value={selectedBundleId || ''}
                          onChange={(e) => setSelectedBundleId(e.target.value)}
                          data-testid="intake-bundle-select"
                        >
                          {intakeBundles.map((b) => (
                            <option key={b.bundle_id} value={b.bundle_id}>{b.bundle_name}</option>
                          ))}
                        </select>
                      )}
                      {includeInBundle && (
                        <p className="text-xs text-gray-500 mt-1.5">
                          The assessment will appear grouped with the intake packet on the Documents tab.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-start pt-4">
                <Button variant="ghost" onClick={() => setSelectedTemplate(null)}>
                  ← Back to Assessment List
                </Button>
              </div>
            </div>
          )}

          {currentStep === 'fill_form' && selectedTemplate?.form_fields && (
            <div className="space-y-4">
              {selectedTemplate.form_fields?.find(f => (f.field_type || f.type) === 'label' || (f.field_type || f.type) === 'static_text') && (
                <p className="text-gray-600 italic bg-gray-50 p-3 rounded-lg">
                  {(() => {
                    const labelField = selectedTemplate.form_fields.find(f => (f.field_type || f.type) === 'label' || (f.field_type || f.type) === 'static_text');
                    return labelField?.default_value || labelField?.content || '';
                  })()}
                </p>
              )}

              {selectedTemplate.form_fields
                ?.filter(f => {
                  const fieldType = f.field_type || f.type;
                  return fieldType !== 'label' && fieldType !== 'static_text';
                })
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(field => {
                  const fieldId = field.field_id || field.id;
                  const fieldName = field.field_name || field.label;
                  const fieldType = field.field_type || field.type;

                  return (
                    <div key={fieldId} className="space-y-2">
                      <Label className="text-sm font-medium">
                        {fieldName}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {fieldType === 'radio' && (
                        <div className="grid grid-cols-2 gap-2">
                          {field.options?.map((option, optIdx) => {
                            const optionLabel = getOptionLabel(option);
                            return (
                              <div
                                key={`${fieldId}-opt-${optIdx}`}
                                className={`p-3 border rounded-lg cursor-pointer text-sm transition-colors ${
                                  formData[fieldId] === optionLabel
                                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                                    : 'hover:bg-gray-50'
                                }`}
                                onClick={() => handleFieldChange(fieldId, optionLabel)}
                              >
                                {optionLabel}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

              <div className="flex justify-between items-center pt-4 border-t">
                <Button variant="ghost" onClick={() => setDeliveryMode(null)}>
                  ← Back
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleClose}>Cancel</Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!allFieldsAnswered || submitting}
                    data-testid="assessment-submit-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit Assessment'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssessmentsTabContent;
